import { useNotification } from "App/State/Hooks/useNotificationHooks";
import { IAttribute, IEditAttribute } from "App/Typings/AbacAttribute";
import { ITableColumn } from "App/Typings/Table";
import { useFormik } from "formik";
import {
  useEditAbacAttributeMutation,
  useGetAllFeatureAttributesForAbacAttributesQuery,
} from "Pages/ABAC/AbacAppQueries";
import { useCallback, useState } from "react";
import { object, string } from "yup";

const columns: ITableColumn[] = [
  { label: "Features Assigned To: ", propName: "" },
  { label: "Is Active", propName: "" },
  { label: "", propName: "" },
];

//Validation for the edit app form
const validationSchema = object().shape({
  attributeName: string()
    .trim()
    .max(100, "Attribute Name must be less than 100 characters")
    .required("Attribute Name is required"),
  attributeDescription: string().max(500, "Attribute Description must be less than 500 characters"),
});

export default function useAbacAttributeDetailsState(currentAttribute: IAttribute) {
  const successMessage = "Successfully updated attribute information!";
  const errorMessage = "Error updating attribute information!";
  const { displaySuccess, displayError } = useNotification();
  const [isActive, setIsActive] = useState(currentAttribute.isActive);
  const { mutate: editAttribute, isLoading: editIsLoading } = useEditAbacAttributeMutation(
    currentAttribute.id,
    currentAttribute.app.id
  );

  const { data: featureAttributes = [], isLoading: featureAttributesLoading } =
    useGetAllFeatureAttributesForAbacAttributesQuery(currentAttribute.id);

  //Initial edit Attribute formik setup
  const formik = useFormik({
    initialValues: {
      attributeName: currentAttribute.name,
      attributeDescription: currentAttribute.description,
    },
    validationSchema: validationSchema,
    // Create the IEditAttribute object to pass to the mutation
    onSubmit: (values) => {
      // Remove whitespace from formik values
      formik.setValues({
        attributeName: values.attributeName.trim(),
        attributeDescription: values.attributeDescription.trim(),
      });
      let editedAttribute: IEditAttribute = {
        id: currentAttribute.id,
        appId: currentAttribute.app.id,
        name: values.attributeName.trim(),
        description: values.attributeDescription.trim(),
        isActive: isActive,
        allowCustomAssign: false,
        isDefault: false,
      };
      submitEditAttribute(editedAttribute);
    },
  });

  // Edit the current ABAC Attribute
  const submitEditAttribute = useCallback(
    function saveAttributeInformation(editedAttribute: IEditAttribute) {
      if (!editIsLoading) {
        editAttribute(editedAttribute, {
          onError: () => {
            displayError(errorMessage);
          },
          onSuccess: () => {
            displaySuccess(successMessage);
          },
        });
      }
    },
    [displaySuccess, displayError, editAttribute, editIsLoading]
  );

  return {
    getters: {
      isActive,
      formik,
      columns,
      featureAttributes,
      featureAttributesLoading,
    },
    setters: {
      setIsActive,
    },
    handlers: {},
  };
}
